import { Fragment, useId, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { announceToScreenReader } from '@/utils/a11y';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  footer?: React.ReactNode;
  description?: string;
  /**
   * Element to focus when modal opens. If not provided, focuses the close button.
   */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /**
   * Whether to announce the modal title to screen readers when it opens
   */
  announceOnOpen?: boolean;
}

const sizeConfig = {
  sm: 'w-full max-w-md mx-0 sm:mx-4',
  md: 'w-full max-w-lg mx-0 sm:mx-4',
  lg: 'w-full max-w-2xl mx-0 sm:mx-4',
  xl: 'w-full max-w-4xl mx-0 sm:mx-4',
  '2xl': 'w-full max-w-6xl mx-0 sm:mx-4',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  footer,
  description,
  initialFocusRef,
  announceOnOpen = true,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Announce modal opening to screen readers
  useEffect(() => {
    if (isOpen && announceOnOpen && title) {
      announceToScreenReader(`Dialog opened: ${title}`, 'assertive');
    }
  }, [isOpen, announceOnOpen, title]);

  // Announce modal closing
  useEffect(() => {
    return () => {
      if (!isOpen) {
        announceToScreenReader('Dialog closed', 'polite');
      }
    };
  }, [isOpen]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        initialFocus={initialFocusRef || closeButtonRef}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-3 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`${sizeConfig[size]} transform overflow-hidden rounded-t-3xl sm:rounded-2xl mm-glass text-left align-middle shadow-2xl transition-all max-h-[92vh] sm:max-h-[85vh] flex flex-col border border-white/10`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={description ? descriptionId : undefined}
              >
                {/* Mobile drag handle indicator */}
                <div className="sm:hidden flex justify-center pt-2 pb-1">
                  <div className="w-10 h-1 rounded-full bg-white/20" aria-hidden="true" />
                </div>

                <div className="flex items-center justify-between border-b border-white/10 px-4 sm:px-6 py-3 sm:py-5 safe-area-top flex-shrink-0 bg-white/5">
                  <Dialog.Title
                    as="h3"
                    id={titleId}
                    className="text-base sm:text-lg font-semibold text-white truncate pr-2"
                  >
                    {title}
                  </Dialog.Title>
                  <button
                    ref={closeButtonRef}
                    onClick={onClose}
                    className="text-white/60 hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 focus:ring-offset-transparent rounded-xl p-2.5 touch-target flex-shrink-0 -mr-2 active:bg-white/10"
                    aria-label="Close dialog"
                  >
                    <X size={22} aria-hidden="true" />
                  </button>
                </div>

                <div className="px-4 sm:px-6 py-4 sm:py-6 overflow-y-auto flex-1 scrollbar-custom overscroll-contain">
                  {description && (
                    <p id={descriptionId} className="sr-only">
                      {description}
                    </p>
                  )}
                  {children}
                </div>

                {footer && (
                  <div className="border-t border-white/10 px-4 sm:px-6 py-4 bg-white/5 flex-shrink-0 safe-area-bottom">{footer}</div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
